import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';

import Placeholder from '../Placeholder';

/**
 * Dashboard fallback UI for asynchronous, imperative errors that are
 * propagated to the error boundary via component state rather than being
 * caught automatically during the React render cycle. These errors typically
 * originate from `fetchQuery` calls surfaced through a hook's error state
 * (for example, `usePropertyData`).
 *
 * The fallback is feature-agnostic: callers pass a pre-localized `bodyText`
 * built from their own vocabulary, so this primitive carries no per-feature
 * message dictionary.
 * @param {object} props - Component props.
 * @param {string|null} props.bodyText - Optional pre-localized modal body
 *   message describing the failed operation. When null, the underlying
 *   modal renders its default body.
 * @param {Error} props.error - Captured async fetch error.
 * @param {Function} props.onCancelFunc - Cancel handler that clears the
 *   underlying hook error state.
 * @param {Function} props.resetFunc - Retry handler that replays the
 *   failed operation.
 * @param {boolean} props.retryDisabled - Disables retry while a replay is in
 *   flight.
 * @returns {React.ReactNode} dashboard async error fallback UI.
 */
function DashboardAsyncErrorFallback({
  bodyText, error, onCancelFunc, resetFunc, retryDisabled,
}) {
  const details = useMemo(() => ({
    message: error?.message || null,
    stack: error?.stack || null,
  }), [error]);

  return (
    <PlaceholderErrorModal
      bodyText={bodyText}
      details={details}
      onCancelFunc={onCancelFunc}
      onRetryFunc={resetFunc}
      placeholderComponent={Placeholder}
      retryDisabled={retryDisabled}
    />
  );
}

DashboardAsyncErrorFallback.propTypes = {
  bodyText: PropTypes.string,
  error: PropTypes.instanceOf(Error).isRequired,
  onCancelFunc: PropTypes.func.isRequired,
  resetFunc: PropTypes.func.isRequired,
  retryDisabled: PropTypes.bool,
};

DashboardAsyncErrorFallback.defaultProps = {
  bodyText: null,
  retryDisabled: false,
};

export default DashboardAsyncErrorFallback;
